import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useCars } from '../context/CarContext';
import { CarFormData } from '../types';
import ImageUpload from './ImageUpload';
import toast from 'react-hot-toast';

interface AddCarFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddCarForm = ({ isOpen, onClose }: AddCarFormProps) => {
  const { addCar } = useCars();
  const [formData, setFormData] = useState<CarFormData>({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: 0,
    mileage: 0,
    color: '',
    fuelType: 'Gasoline',
    transmission: 'Automatic',
    condition: 'Used',
    description: '',
    images: [],
    features: [],
    saleStatus: 'available',
  });

  const [newFeature, setNewFeature] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: keyof CarFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle uploaded images from ImageUpload component
  const handleImagesUploaded = (newImageUrls: string[]) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...newImageUrls]
    }));
    toast.success(`${newImageUrls.length} imagen(es) subida(s) exitosamente`);
  };

  const addFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addImage = () => {
    if (newImageUrl.trim() && !formData.images.includes(newImageUrl.trim())) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const requiredFields = ['make', 'model', 'color', 'description'];
    const missingFields = requiredFields.filter(field => !formData[field as keyof CarFormData]);
    
    if (missingFields.length > 0) {
      toast.error(`Campos requeridos: ${missingFields.join(', ')}`);
      return false;
    }

    if (formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      toast.error('Año inválido');
      return false;
    }

    if (formData.price <= 0) {
      toast.error('El precio debe ser mayor a 0');
      return false;
    }

    if (formData.mileage < 0) {
      toast.error('El millaje no puede ser negativo');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Las imágenes ya fueron subidas a Google Cloud Storage via ImageUpload component
      // y sus URLs están en formData.images
      
      // Guardar datos del auto en Firestore con las URLs de imágenes de GCS
      await addCar({
        ...formData,
        isAvailable: true
      });

      toast.success('¡Vehículo agregado exitosamente!');
      
      // Reset form
      setFormData({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        price: 0,
        mileage: 0,
        color: '',
        fuelType: 'Gasoline',
        transmission: 'Automatic',
        condition: 'Used',
        description: '',
        images: [],
        features: [],
        saleStatus: 'available',
      });
      
      onClose();
    } catch (error) {
      toast.error('Error al agregar el vehículo');
      console.error('Error adding car:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <h2 className="text-xl font-semibold text-gray-900">Agregar Nuevo Vehículo</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Marca *
              </label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => handleInputChange('make', e.target.value)}
                className="input-field"
                placeholder="Ej: Toyota, Honda, Ford..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Modelo *
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                className="input-field"
                placeholder="Ej: Corolla, Civic, Focus..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Año *
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                className="input-field"
                min="1900"
                max={new Date().getFullYear() + 1}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Precio (USD) *
              </label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                className="input-field"
                min="0"
                step="0.01"
                placeholder="25000"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Millaje
              </label>
              <input
                type="number"
                value={formData.mileage}
                onChange={(e) => handleInputChange('mileage', parseInt(e.target.value))}
                className="input-field"
                min="0"
                placeholder="50000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color *
              </label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                className="input-field"
                placeholder="Blanco, Negro, Azul..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Combustible
              </label>
              <select
                value={formData.fuelType}
                onChange={(e) => handleInputChange('fuelType', e.target.value)}
                className="input-field"
              >
                <option value="Gasoline">Gasolina</option>
                <option value="Diesel">Diésel</option>
                <option value="Electric">Eléctrico</option>
                <option value="Hybrid">Híbrido</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transmisión
              </label>
              <select
                value={formData.transmission}
                onChange={(e) => handleInputChange('transmission', e.target.value)}
                className="input-field"
              >
                <option value="Automatic">Automática</option>
                <option value="Manual">Manual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Condición
              </label>
              <select
                value={formData.condition}
                onChange={(e) => handleInputChange('condition', e.target.value)}
                className="input-field"
              >
                <option value="New">Nuevo</option>
                <option value="Used">Usado</option>
                <option value="Certified Pre-Owned">Certificado Pre-Owned</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado de Venta
              </label>
              <select
                value={formData.saleStatus}
                onChange={(e) => handleInputChange('saleStatus', e.target.value)}
                className="input-field"
              >
                <option value="available">Disponible</option>
                <option value="pending">Pendiente</option>
                <option value="sold">Vendido</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="input-field"
              rows={4}
              placeholder="Describe las características principales del vehículo, su estado, historial, etc."
              required
            />
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Imágenes del Vehículo
            </label>
            
            {/* Image Upload Component */}
            <div className="mb-6">
              <ImageUpload
                onImagesUploaded={handleImagesUploaded}
                maxImages={10}
                existingImages={formData.images}
              />
            </div>

            {/* Manual URL Input (fallback option) */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                O agregar URL de imagen manualmente
              </h4>
              <div className="flex gap-2 mb-3">
                <input
                  type="url"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  className="input-field flex-1"
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
                <button
                  type="button"
                  onClick={addImage}
                  className="btn-secondary flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Agregar URL</span>
                </button>
              </div>
            </div>
              
            {/* Image Preview List */}
            {formData.images.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Imágenes agregadas ({formData.images.length}/10)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {formData.images.map((url, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/48x48/e5e7eb/6b7280?text=?';
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-600 truncate block">
                            {url.includes('bitbucket.org') ? 
                              `🔗 Imagen ${index + 1} (Bitbucket)` : 
                              url.length > 30 ? `${url.substring(0, 30)}...` : url
                            }
                          </span>
                          {url.includes('bitbucket.org') && (
                            <span className="text-xs text-green-600">Subida a Bitbucket</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Eliminar imagen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Características
            </label>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  className="input-field flex-1"
                  placeholder="Ej: Aire acondicionado, GPS, Bluetooth..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="btn-secondary flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Agregar</span>
                </button>
              </div>
              
              {formData.features.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : 'Agregar Vehículo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCarForm;